// Sidebar.jsx
import {
  History,
  ChevronDown,
  ChevronRight,
  Bot,
  Zap,
  Route,
  Rss,
  Award,
  MessageSquareQuote,
  SquareArrowOutUpRight,
  ChartNoAxesColumn,
  ChartNoAxesCombined,
  GitCompareArrows,
} from "lucide-react";
import React, { useState, useEffect } from "react";
import { AGENT_COLORS } from "./utils/tools";

const ORCHESTRATOR_NAME = "Orchestrator Agent";
const ORCHESTRATOR_COLOR = "#EC10A3";
const ORCHESTRATOR_BG = "rgba(255,255,255,0.08)";
const SHOW_RECENT_CHATS = false;

// Map each agent to an external link (placeholders — replace with real URLs)
const AGENT_LINKS = {
  [ORCHESTRATOR_NAME]: "https://example.com/orchestrator",
  "Spend Analytics Agent": "https://cube.kearney.com/login",
  "Market Intel Agent": "https://magiccube.kearney.com/",
  "Supplier Risk Agent": "http://kpsgpt.kearney.com/supplier-dashboard",
  "Supplier Discovery Agent": "https://kpsgpt.kearney.com/spendGPT",
  "Benchmarking Agent": "https://webappuicttfnonprod.azurewebsites.net/opportunity-alerts/overview ",
  "Opportunity Identification Agent": "https://contractpulsestg.ai.kearney.com/homepage",
  "Price Projection Agent": "https://contractpulsestg.ai.kearney.com/homepage",
};

const styles = {
  sidebar: {
    position: "fixed",
    left: 0,
    top: 60,
    width: "260px",
    height: "calc(100vh - 60px)",
    backgroundColor: "#111111",
    borderRight: "1px solid #2a2a2a",
    display: "flex",
    flexDirection: "column",
    padding: "1rem 0",
    overflow: "hidden",
    zIndex: 100,
  },
  navigation: {
    flex: 1,
    padding: "0 1rem",
    overflowY: "auto",
  },
  navSection: {
    marginBottom: "1.5rem",
  },
  navTitle: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    color: "#ffffff",
    fontSize: "0.875rem",
    fontWeight: "500",
    marginBottom: "0.5rem",
    letterSpacing: "0.05em",
    cursor: "pointer",
    padding: "0.25rem 0",
  },
  navTitleLeft: {
    display: "flex",
    alignItems: "center",
    gap: "0.5rem",
  },
  navTitleRight: {
    color: "#9ca3af",
    fontSize: "0.75rem",
    fontWeight: "400",
  },
  chevronIcon: {
    width: "14px",
    height: "14px",
    transition: "transform 0.15s ease",
  },
  navItem: {
    display: "flex",
    alignItems: "center",
    gap: "0.75rem",
    padding: "0.5rem 0.75rem",
    color: "#9ca3af",
    backgroundColor: "transparent",
    border: "none",
    borderRadius: "6px",
    fontSize: "0.875rem",
    cursor: "pointer",
    width: "100%",
    textAlign: "left",
    transition: "all 0.15s ease",
    marginBottom: "0.25rem",
    justifyContent: "space-between", // to push external icon to the right
  },
  navIcon: {
    // <-- re-added
    fontSize: "16px",
    minWidth: "16px",
  },
  agentIcon: {
    width: "16px",
    height: "16px",
    minWidth: "16px",
  },
  // Active (animated) agent item
  agentItemAnimated: {
    position: "relative",
    display: "flex",
    alignItems: "center",
    gap: "0.75rem",
    padding: "0.5rem 0.75rem",
    color: "#ffffff",
    backgroundColor: "rgba(255,255,255,0.05)",
    border: "1px solid transparent",
    borderRadius: "6px",
    fontSize: "0.875rem",
    cursor: "pointer",
    width: "100%",
    textAlign: "left",
    transition: "all 0.15s ease",
    marginBottom: "0.25rem",
    justifyContent: "space-between", // to push external icon to the right
  },
  recentChats: {
    flex: 1,
    overflowY: "auto",
  },
  chatItem: {
    display: "flex",
    alignItems: "center",
    gap: "0.75rem",
    padding: "0.5rem 0.75rem",
    color: "#9ca3af",
    backgroundColor: "transparent",
    border: "none",
    borderRadius: "8px",
    fontSize: "0.875rem",
    cursor: "pointer",
    width: "100%",
    textAlign: "left",
    transition: "all 0.15s ease",
    marginBottom: "0.25rem",
  },
  bottomActions: {
    padding: "0.8rem 1rem 1.2rem",
    marginTop: "auto",
    borderTop: "none",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  poweredBy: {
    textAlign: "center",
    color: "rgba(255,255,255,0.62)",
    fontSize: "8.5px",
    textTransform: "uppercase",
    letterSpacing: "1.6px",
    lineHeight: 1.35,
    fontFamily: "'Graphik Web', 'Helvetica Neue', Arial, sans-serif",
    fontWeight: 500,
  },
  poweredByBrand: {
    display: "block",
    fontSize: "13px",
    fontWeight: 500,
    letterSpacing: "2.8px",
    color: "rgba(255,255,255,0.9)",
    fontFamily: "'Graphik Web', 'Helvetica Neue', Arial, sans-serif",
  },
  collapsibleContent: {
    overflow: "hidden",
    transition: "max-height 0.3s ease",
  },
};

// CSS for the animated border - inject once
const animatedBorderCSS = `
  @property --angle {
    syntax: '<angle>';
    inherits: false;
    initial-value: 0deg;
  }
  @keyframes agent-border-sweep {
    to { --angle: 360deg; }
  }
  .agent-animated-border {
    position: relative;
    --angle: 0deg;
  }
  .agent-animated-border::before {
    content: "";
    position: absolute;
    inset: 0;    /* keep the ring inside the button so it won't be clipped */
    border-radius: inherit;
    background: conic-gradient(
      from var(--angle),
      var(--agent-color) 0%,
      transparent 35%,
      var(--agent-color) 50%,
      transparent 85%,
      var(--agent-color) 100%
    );
    padding: 1px; /* inner ring thickness */
    -webkit-mask:
      linear-gradient(#000 0 0) content-box,
      linear-gradient(#000 0 0);
    -webkit-mask-composite: xor;
            mask-composite: exclude;
    animation: agent-border-sweep 1.2s linear infinite;
    pointer-events: none;
  }
`;

export const agents = [
  { name: ORCHESTRATOR_NAME, icon: Route },
  { name: "Spend Analytics Agent", icon: Bot },
  { name: "Market Intel Agent", icon: Rss },
  { name: "Supplier Risk Agent", icon: Zap },
  { name: "Supplier Discovery Agent", icon: GitCompareArrows },
  { name: "Benchmarking Agent", icon: ChartNoAxesCombined },
  { name: "Opportunity Identification Agent", icon: MessageSquareQuote },
  // { name: "Price Projection Agent", icon: ChartNoAxesCombined },
];

export const Sidebar = ({
  onReset,
  onResetMemory,
  isResettingMem,
  activeAgents,
  isAnalyzing, // pass true while a query/run is active
}) => {
  const [isAgentsExpanded, setIsAgentsExpanded] = useState(true);
  const [isRecentsExpanded, setIsRecentsExpanded] = useState(true);

  // Inject the CSS once
  useEffect(() => {
    const styleId = "agent-animated-border-styles";
    if (!document.getElementById(styleId)) {
      const style = document.createElement("style");
      style.id = styleId;
      style.textContent = animatedBorderCSS;
      document.head.appendChild(style);
    }
  }, []);

  const recentChats = [
    "Top Supplier Analysis",
    "List of All Supplier in the...",
    "Suppliers based on locat...",
  ];

  // Helper: is the given agent active?
  const isAgentActive = (agentName) => {
    if (agentName === ORCHESTRATOR_NAME) {
      // Orchestrator is active for the entire run
      return isAnalyzing || activeAgents.includes(ORCHESTRATOR_NAME);
    }
    return activeAgents.includes(agentName);
  };

  // Helper: color per agent (active = agent color, inactive = gray)
  const getAgentColor = (agentName, active) => {
    if (agentName === ORCHESTRATOR_NAME) {
      return active ? ORCHESTRATOR_COLOR : "#9ca3af";
    }
    return active ? AGENT_COLORS[agentName] || "#6EE7F9" : "#9ca3af";
  };

  return (
    <div style={styles.sidebar}>
      <div style={styles.navigation}>
        {/* New Chat (➕ restored) */}
        <div style={styles.navSection}>
          <div
            style={{
              backgroundColor: "#FFFFFF0F",
              borderRadius: "8px",
            }}
          >
            <button style={styles.navItem} onClick={onReset}>
              <span
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "0.75rem",
                }}
              >
                <span style={styles.navIcon}>➕</span>
                New Chat
              </span>
            </button>
          </div>
        </div>

        {/* Agents Section */}
        <div style={styles.navSection}>
          <div
            style={styles.navTitle}
            onClick={() => setIsAgentsExpanded(!isAgentsExpanded)}
          >
            <div style={styles.navTitleLeft}>
              {isAgentsExpanded ? (
                <ChevronDown style={styles.chevronIcon} />
              ) : (
                <ChevronRight style={styles.chevronIcon} />
              )}
              Agents
            </div>
            <span style={styles.navTitleRight}>{agents.length}</span>
          </div>

          <div
            style={{
              ...styles.collapsibleContent,
              maxHeight: isAgentsExpanded ? "320px" : "0px",
            }}
          >
            {agents.map((agent) => {
              const IconComponent = agent.icon;
              const active = isAgentActive(agent.name);
              const color = getAgentColor(agent.name, active);
              const link = AGENT_LINKS[agent.name] || "https://example.com";

              // active orchestrator
              if (agent.name === ORCHESTRATOR_NAME && active) {
                return (
                  <button
                    key={agent.name}
                    className="agent-animated-border"
                    style={{
                      ...styles.agentItemAnimated,
                      backgroundColor: ORCHESTRATOR_BG,
                      ["--agent-color"]: ORCHESTRATOR_COLOR,
                    }}
                  >
                    <span
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "0.75rem",
                      }}
                    >
                      <IconComponent
                        style={{
                          ...styles.agentIcon,
                          color: ORCHESTRATOR_COLOR,
                        }}
                      />
                      {agent.name}
                    </span>
                  </button>
                );
              }

              // inactive orchestrator
              if (agent.name === ORCHESTRATOR_NAME && !active) {
                return (
                  <button key={agent.name} style={styles.navItem}>
                    <span
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "0.75rem",
                      }}
                    >
                      <IconComponent style={styles.agentIcon} />
                      {agent.name}
                    </span>
                  </button>
                );
              }

              // other agents - active
              if (active) {
                return (
                  <button
                    key={agent.name}
                    className="agent-animated-border"
                    style={{
                      ...styles.agentItemAnimated,
                      ["--agent-color"]: color,
                    }}
                  >
                    <span
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "0.75rem",
                      }}
                    >
                      <IconComponent
                        style={{
                          ...styles.agentIcon,
                          color,
                        }}
                      />
                      {agent.name}
                    </span>
                    <a
                      href={link}
                      target="_blank"
                      rel="noopener noreferrer"
                      title="Open in new tab"
                      style={{ color }}
                      onClick={(e) => e.stopPropagation()}
                    >
                      <SquareArrowOutUpRight
                        style={{ width: 12, height: 12, minWidth: 12 }}
                      />
                    </a>
                  </button>
                );
              }

              // other agents - inactive
              return (
                <button key={agent.name} style={styles.navItem}>
                  <span
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "0.75rem",
                    }}
                  >
                    <IconComponent style={styles.agentIcon} />
                    {agent.name}
                  </span>
                  <a
                    href={link}
                    target="_blank"
                    rel="noopener noreferrer"
                    title="Open in new tab"
                    style={{ color }}
                    onClick={(e) => e.stopPropagation()}
                  >
                    <SquareArrowOutUpRight
                      style={{ width: 12, height: 12, minWidth: 12 }}
                    />
                  </a>
                </button>
              );
            })}
          </div>
        </div>

        {/* Recent Chats Section - hidden for now */}
        {SHOW_RECENT_CHATS && (
          <div style={styles.navSection}>
            <div
              style={styles.navTitle}
              onClick={() => setIsRecentsExpanded(!isRecentsExpanded)}
            >
              <div style={styles.navTitleLeft}>
                {isRecentsExpanded ? (
                  <ChevronDown style={styles.chevronIcon} />
                ) : (
                  <ChevronRight style={styles.chevronIcon} />
                )}
                Recent Chats
              </div>
              <span style={styles.navTitleRight}>{recentChats.length}</span>
            </div>

            <div
              style={{
                ...styles.collapsibleContent,
                maxHeight: isRecentsExpanded ? "400px" : "0px",
                overflowY: "auto",
              }}
            >
              {recentChats.map((chat, index) => (
                <button
                  key={index}
                  style={styles.chatItem}
                  onMouseOver={(e) =>
                    Object.assign(e.currentTarget.style, {
                      backgroundColor: "#1a1a1a",
                      color: "#ffffff",
                    })
                  }
                  onMouseOut={(e) =>
                    Object.assign(e.currentTarget.style, styles.chatItem)
                  }
                >
                  <History style={styles.agentIcon} />
                  {chat}
                </button>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Bottom Actions (optional) */}
      <div style={styles.bottomActions}>
        <div style={styles.poweredBy}>
          <span>Powered By</span>
          <span style={styles.poweredByBrand}>Kearney</span>
        </div>
      </div>
    </div>
  );
};
